const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === '') {
        return null
    }
    const cleaned = typeof value === 'string' ? value.replace(/[^\d.-]/g, '') : value
    if (cleaned === '') {
        return null
    }
    const parsed = Number(cleaned)
    return Number.isFinite(parsed) ? parsed : null
}

const normalizeCurrencyCode = (value) =>
    String(value || '')
        .trim()
        .toUpperCase()

const parseMaybeJsonObject = (value) => {
    if (!value) {
        return null
    }
    if (typeof value === 'object') {
        return value
    }
    if (typeof value !== 'string') {
        return null
    }
    try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === 'object' ? parsed : null
    } catch (error) {
        return null
    }
}

const getVendorCustomFields = (item) => parseMaybeJsonObject(item?.custom_vendor_fields) || {}

const getPreferredNumericValue = (...values) => {
    const parsedValues = values
        .map((value) => toNumberOrNull(value))
        .filter((value) => value !== null)

    // Some non-API vendor rows persist 0 in the standardized price columns even when a
    // real raw price exists in price tiers or custom vendor fields. Prefer the first
    // positive value when available, but still allow a real 0 if that is all we have.
    const firstPositiveValue = parsedValues.find((value) => value > 0)
    return firstPositiveValue ?? parsedValues[0] ?? null
}

const getFirstNumericValue = (...values) => {
    for (const value of values) {
        const parsed = toNumberOrNull(value)
        if (parsed !== null) {
            return parsed
        }
    }
    return null
}

const formatSourceVendorName = (value) => {
    if (!value) {
        return ''
    }
    return value
        .toString()
        .trim()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

const normalizeInventoryLabel = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '')

// Legacy safeguard: old dashboard search sessions could persist inventory rows in the
// vendor dataset, so the UI still recognizes those labels and keeps them out of Vendor.
const INVENTORY_VENDOR_LABELS = new Set([
    'greenwoodponcainventory',
    'globalpartsinventory',
    'inventorycomponent',
    'inventorycomponentcontrol',
    'inventoryglobalparts',
])

export const isInventorySource = (value) =>
    INVENTORY_VENDOR_LABELS.has(normalizeInventoryLabel(value))

export const isInventoryRow = (item) =>
    isInventorySource(item?.source) ||
    isInventorySource(item?.vendor) ||
    isInventorySource(item?.ui_vendor_name)

export const normalizePriceTiers = (pricePerQty) => {
    if (!pricePerQty) {
        return []
    }

    let parsedValue = pricePerQty
    if (typeof parsedValue === 'string') {
        try {
            parsedValue = JSON.parse(parsedValue)
        } catch (error) {
            return []
        }
    }

    const rawTiers = Array.isArray(parsedValue)
        ? parsedValue
        : parsedValue && typeof parsedValue === 'object'
          ? [parsedValue]
          : []

    return rawTiers
        .map((entry) => {
            if (!entry || typeof entry !== 'object') {
                return null
            }
            const qty = toNumberOrNull(entry.qty ?? entry.qtd ?? entry.quantity ?? null)
            const unitPrice = toNumberOrNull(
                entry.unit_price ?? entry.price ?? entry.your_price ?? entry.list_price ?? null
            )
            const currency = normalizeCurrencyCode(entry.currency ?? entry.Currency ?? '')
            return { qty, unit_price: unitPrice, currency }
        })
        .filter((entry) => entry !== null)
}

export const firstTier = (itemOrTiers) => {
    const tiers = normalizePriceTiers(itemOrTiers?.price_per_qty ?? itemOrTiers)
    return tiers.length > 0 ? tiers[0] : null
}

export const getVendorLabel = (item) =>
    item?.ui_vendor_name || item?.vendor || formatSourceVendorName(item?.source) || ''

export const getVendorForQuote = (itemOrValue) => {
    const rawValue =
        typeof itemOrValue === 'object' && itemOrValue !== null
            ? getVendorLabel(itemOrValue)
            : itemOrValue
    const raw = String(rawValue ?? '').trim()
    const normalized = raw.toLowerCase()

    if (normalized === 'boeing api' || normalized === 'boeing' || normalized === 'boeing/aviall') {
        return 'Boeing'
    }

    if (normalized === 'textron defense') {
        return 'Textron Aviation Defense'
    }

    return raw
}

export const getSubstituteForDisplay = (
    item,
    principalParts = [],
    substituteParentMap = {}
) => {
    const value = item?.substitute_for
    const principalCandidates = [
        item?.part_number,
        item?.part_number_manufacturer,
        item?.vendor_part_number,
    ]
        .filter(Boolean)
        .map((candidate) => String(candidate).trim())

    const explicitPrincipalCandidates = principalParts
        .filter(Boolean)
        .map((candidate) => String(candidate).trim())

    const isPrincipalRow = principalCandidates.some((candidate) =>
        explicitPrincipalCandidates.includes(candidate)
    )

    if (isPrincipalRow) {
        return ''
    }

    const mappedParent = principalCandidates
        .map((candidate) => substituteParentMap?.[candidate])
        .find(Boolean)

    if (mappedParent) {
        return mappedParent
    }

    if (!Array.isArray(value)) {
        return value || ''
    }

    const normalizedValues = value.map((entry) => String(entry || '').trim())

    const matchingPrincipal = [...explicitPrincipalCandidates, ...principalCandidates].find((candidate) =>
        normalizedValues.includes(candidate)
    )
    const firstParentCandidate = normalizedValues.find(
        (candidate) => candidate && !principalCandidates.includes(candidate)
    )

    return matchingPrincipal || firstParentCandidate || normalizedValues[0] || ''
}

export const getPartCandidates = (item) => {
    const rawCandidates = [
        item?.part_number,
        item?.part_number_manufacturer,
        item?.vendor_part_number,
        item?.substitute_for,
    ]

    const cleaned = rawCandidates
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter(Boolean)
        .map((value) => value.toString().trim())
        .map((value) => (value.includes('=') ? value.split('=')[0] : value))
        .filter(Boolean)

    return Array.from(new Set(cleaned))
}

export const getDisplayQty = (item) =>
    getPrimaryTextronQty(item) ??
    toNumberOrNull(item?.qty_in_stock ?? item?.quantity ?? item?.on_hand_qty) ??
    item?.display_qty ??
    firstTier(item)?.qty ??
    null

export const isTextronVendor = (item) => {
    const source = (item?.source || '').toString().trim().toLowerCase()
    const vendorLabel = (item?.ui_vendor_name || '').toString().trim().toLowerCase()
    return source === 'textron' || vendorLabel === 'textron'
}

const getPrimaryTextronQty = (item) => {
    if (!isTextronVendor(item)) {
        return null
    }

    const rawLocations =
        item?.custom_vendor_fields?.locations ??
        item?.custom_vendor_fields?.Locations ??
        item?.locations ??
        item?.Locations ??
        []

    const locations = Array.isArray(rawLocations) ? rawLocations : []

    const wichitaLocation = locations
        .map((entry) => parseMaybeJsonObject(entry))
        .find((entry) => {
            const location = (entry?.location ?? entry?.Location ?? '').toString().trim()
            return location.toLowerCase() === 'wichita'
        })

    if (!wichitaLocation) {
        return null
    }

    return toNumberOrNull(
        wichitaLocation.quantity_avail ??
            wichitaLocation.QuantityAvail ??
            wichitaLocation.quantity ??
            null
    )
}

export const getServiceCenters = (item) => {
    const raw =
        item?.custom_vendor_fields?.service_centers ??
        item?.custom_vendor_fields?.ServiceCenters ??
        item?.service_centers ??
        item?.ServiceCenters ??
        []

    const centers = Array.isArray(raw) ? raw : []
    const normalizedCenters = centers
        .map((entry) => parseMaybeJsonObject(entry))
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry, index) => {
            const location = entry.location ?? entry.Location ?? ''
            const quantityAvail = toNumberOrNull(
                entry.quantity_avail ?? entry.QuantityAvail ?? entry.quantity ?? null
            )
            const price = toNumberOrNull(entry.price ?? entry.Price ?? null)
            const listPrice = toNumberOrNull(entry.list_price ?? entry.ListPrice ?? null)
            const currency = normalizeCurrencyCode(entry.currency ?? entry.Currency ?? '')
            const potentialAvailableDate =
                entry.potential_available_date ?? entry.PotentialAvailableDate ?? ''
            const category = entry.category ?? entry.Category ?? ''

            return {
                key: `${location || 'service-center'}-${index}`,
                location: location || 'N/A',
                quantity_avail: quantityAvail,
                price,
                list_price: listPrice,
                currency,
                potential_available_date: potentialAvailableDate || '',
                category: category || '',
            }
        })

    const currencyRank = (currency) => {
        const normalizedCurrency = normalizeCurrencyCode(currency)
        if (normalizedCurrency === 'USD') {
            return 0
        }
        if (normalizedCurrency === 'EUR') {
            return 1
        }
        return 2
    }

    return normalizedCenters.sort((a, b) => {
        const byCurrency = currencyRank(a.currency) - currencyRank(b.currency)
        if (byCurrency !== 0) {
            return byCurrency
        }

        return a.location.localeCompare(b.location)
    })
}

export const getServiceCentersSummary = (item) => {
    const serviceCenters = getServiceCenters(item)
    if (serviceCenters.length === 0) {
        return null
    }

    const isOutsideUs = (center) => {
        const location = (center?.location || '').toString().trim().toLowerCase()
        const currency = normalizeCurrencyCode(center?.currency)

        if (currency && currency !== 'USD') {
            return true
        }

        // Location fallback for rare non-US entries using USD.
        return ['brazil', 'dusseldorf', 'paris', 'prague', 'zurich'].some((token) =>
            location.includes(token)
        )
    }

    const totalQty = serviceCenters.reduce(
        (sum, center) => sum + (Number.isFinite(center.quantity_avail) ? center.quantity_avail : 0),
        0
    )
    const usQty = serviceCenters.reduce((sum, center) => {
        const qty = Number.isFinite(center.quantity_avail) ? center.quantity_avail : 0
        return isOutsideUs(center) ? sum : sum + qty
    }, 0)
    const outsideUsQty = serviceCenters.reduce((sum, center) => {
        const qty = Number.isFinite(center.quantity_avail) ? center.quantity_avail : 0
        return isOutsideUs(center) ? sum + qty : sum
    }, 0)
    const preferredPrices = serviceCenters
        .filter((center) => normalizeCurrencyCode(center.currency) !== 'EUR')
        .map((center) => center.price)
        .filter((value) => Number.isFinite(value))
    const minPrice = preferredPrices.length > 0 ? Math.min(...preferredPrices) : null
    const minPriceCenter =
        minPrice == null
            ? null
            : serviceCenters.find(
                  (center) =>
                      Number.isFinite(center.price) &&
                      center.price === minPrice &&
                      normalizeCurrencyCode(center.currency) !== 'EUR'
              ) || null

    return {
        centers_count: serviceCenters.length,
        total_qty: totalQty,
        us_qty: usQty,
        outside_us_qty: outsideUsQty,
        min_price: minPrice,
        min_price_currency: minPriceCenter?.currency || '',
        service_centers: serviceCenters,
    }
}

const getPreferredUnitPrice = (item, tier = firstTier(item)) => {
    const customVendorFields = getVendorCustomFields(item)

    return getPreferredNumericValue(
        item?.your_price,
        item?.unit_price,
        tier?.unit_price,
        item?.price,
        customVendorFields?.price
    )
}

export const getYourPriceMeta = (item) => {
    const tier = firstTier(item)
    const value = getPreferredUnitPrice(item, tier)
    const currency = normalizeCurrencyCode(item?.currency ?? item?.Currency ?? tier?.currency ?? '')

    return {
        value: toNumberOrNull(value),
        currency,
    }
}

export const getListPriceMeta = (item) => {
    const tier = firstTier(item)
    const customVendorFields = getVendorCustomFields(item)
    const value = getPreferredNumericValue(
        item?.list_price,
        item?.ListPrice,
        customVendorFields?.list_price,
        customVendorFields?.ListPrice,
        customVendorFields?.price,
        customVendorFields?.Price,
        item?.price,
        item?.Price,
        tier?.list_price
    )
    const currency = normalizeCurrencyCode(item?.currency ?? item?.Currency ?? tier?.currency ?? '')

    return {
        value: toNumberOrNull(value),
        currency,
    }
}

export const getDisplayPriceMeta = getYourPriceMeta

export const getQuoteCoreDeposit = (item) => {
    const customVendorFields = getVendorCustomFields(item)

    return getPreferredNumericValue(
        item?.core_deposit,
        item?.CoreDeposit,
        customVendorFields?.core_deposit,
        customVendorFields?.CoreDeposit
    )
}

export const getQuoteUnitCost = (item) => {
    const yourPrice = getYourPriceMeta(item).value
    const coreDeposit = getQuoteCoreDeposit(item)

    if (yourPrice == null) {
        return null
    }

    return yourPrice + (coreDeposit ?? 0)
}

export const getQuoteListPrice = (item) => getListPriceMeta(item).value

export const formatCurrencyAmount = (value, currency = 'USD') => {
    const numericValue = toNumberOrNull(value)
    if (numericValue == null) {
        return ''
    }

    const normalizedCurrency = normalizeCurrencyCode(currency) || 'USD'

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: normalizedCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numericValue)
    } catch (error) {
        return `${normalizedCurrency} ${numericValue.toFixed(2)}`
    }
}

export const getDiscountValue = (item) => {
    const customVendorFields = getVendorCustomFields(item)
    const explicitDiscount = getFirstNumericValue(
        item?.discount,
        item?.Discount,
        customVendorFields?.discount,
        customVendorFields?.Discount
    )

    if (explicitDiscount !== null) {
        return explicitDiscount
    }

    const listPrice = getListPriceMeta(item).value
    const yourPrice = getYourPriceMeta(item).value

    if (listPrice == null || yourPrice == null || listPrice <= 0) {
        return null
    }

    return Number((((listPrice - yourPrice) / listPrice) * 100).toFixed(2))
}

export const normalizeVendorRow = (item) => {
    if (!item || typeof item !== 'object') {
        return item
    }

    const normalized = { ...item }
    const tiers = normalizePriceTiers(normalized.price_per_qty)
    normalized.price_per_qty = tiers
    normalized.ui_vendor_name = getVendorLabel(normalized)
    const preferredUnitPrice = getPreferredUnitPrice(normalized, tiers[0])

    if (!normalized.currency) {
        normalized.currency = normalizeCurrencyCode(normalized.Currency ?? tiers[0]?.currency ?? '')
    }

    if (preferredUnitPrice != null) {
        normalized.unit_price = preferredUnitPrice
        // Keep the normalized row internally consistent for the quote modal and any
        // downstream code that still reads your_price directly from vendor rows.
        if (toNumberOrNull(normalized.your_price) == null || Number(normalized.your_price) === 0) {
            normalized.your_price = preferredUnitPrice
        }
    } else if (normalized.unit_price == null) {
        normalized.unit_price = tiers.length > 0 ? tiers[0].unit_price : null
    }

    const listPrice = getQuoteListPrice(normalized)
    if (listPrice != null && toNumberOrNull(normalized.list_price) == null) {
        normalized.list_price = listPrice
    }

    const discount = getDiscountValue(normalized)
    if (discount !== null) {
        normalized.discount = discount
    }

    if (normalized.display_qty == null) {
        normalized.display_qty = getDisplayQty(normalized)
    }

    return normalized
}
